import React, { useEffect, useState } from "react";
import { Spinner, Badge, OverlayTrigger, Tooltip, Modal, Button, Table } from "react-bootstrap";
import { FiClock, FiAlertCircle, FiCheckCircle, FiInfo, FiX, FiTrendingUp, FiShoppingBag, FiList, FiLayers, FiFileText } from "react-icons/fi";

const FESTIVITA_ITALIANE = [
  "01-01", "01-06", "04-25", "05-01", "06-02", "08-15", "11-01", "12-08", "12-25", "12-26",
];

const getLunedediAngelelo = (anno) => {
  const a = anno % 19;
  const b = Math.floor(anno / 100);
  const c = anno % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mese = Math.floor((h + l - 7 * m + 114) / 31);
  const giorno = ((h + l - 7 * m + 114) % 31) + 1;

  const pasqua = new Date(anno, mese - 1, giorno);
  pasqua.setDate(pasqua.getDate() + 1);
  return pasqua;
};

const isFestivoOWeekend = (data) => {
  const giornoSettimana = data.getDay();
  if (giornoSettimana === 0 || giornoSettimana === 6) return true;

  const mmDd = `${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
  if (FESTIVITA_ITALIANE.includes(mmDd)) return true;

  const pasquetta = getLunedediAngelelo(data.getFullYear());
  if (data.toDateString() === pasquetta.toDateString()) return true;

  return false;
};

const calcolaGiorniLavorativiEffettivi = (dataInizio, dataFine) => {
  if (!dataFine || isNaN(dataFine.getTime()) || dataFine <= dataInizio) return 0;

  let giorniLavorativi = 0;
  let dataCorrente = new Date(dataInizio);
  dataCorrente.setDate(dataCorrente.getDate() + 1);

  while (dataCorrente <= dataFine) {
    if (!isFestivoOWeekend(dataCorrente)) {
      giorniLavorativi++;
    }
    dataCorrente.setDate(dataCorrente.getDate() + 1);
  }

  return giorniLavorativi;
};

const estraiCampo = (obj, chiavi, fallback = "N/D") => {
  for (const k of chiavi) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== "") return obj[k];
  }
  return fallback;
};

const MediaFornitoriCard = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedFornitore, setSelectedFornitore] = useState(null);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((json) => {
        const entrate = json?.ENTRATE || (Array.isArray(json) ? json : []);
        const perFornitore = {};
        const annoCorrente = new Date().getFullYear();

        entrate.forEach((e) => {
          const dataCreazione = new Date(e["Data Creazione"]);
          if (isNaN(dataCreazione) || dataCreazione.getFullYear() !== annoCorrente) return;

          const numDoc = e["Num Doc"];
          let targetSpedizione = (parseInt(e["Giorni Spedizione"]) || 0) - 1;

          const isDopoCutoff = dataCreazione.getHours() >= 16;
          if (isDopoCutoff) {
            targetSpedizione += 1;
          }

          if (targetSpedizione < 0) return;

          const fornitore = e["Ragione sociale"] || "N/A";
          
          // Estrazione Data Entrata e Data Fattura/Rimesso (priorità a Data Rimesso, poi data_fatt, poi data_bolla)
          const dataMagazzino = new Date(e["data_entrata"]);
          const stringaFattura = e["Data Rimesso"] || e["data_fatt"] || e["data_bolla"];
          const dataFattura = stringaFattura ? new Date(stringaFattura) : null;

          if (isNaN(dataMagazzino.getTime())) return;

          const articolo = estraiCampo(e, ["articolo", "Articolo", "ARTICOLO", "Descrizione Articolo"]);
          const precodice = estraiCampo(e, ["precodice", "Precodice", "PRECODICE", "Pre Codice", "Pre-Codice"]);

          const diffDaysMag = calcolaGiorniLavorativiEffettivi(dataCreazione, dataMagazzino);
          const diffDaysFatt = dataFattura && !isNaN(dataFattura.getTime()) 
            ? calcolaGiorniLavorativiEffettivi(dataCreazione, dataFattura) 
            : null;

          if (!perFornitore[fornitore]) {
            perFornitore[fornitore] = {
              idFornitore: e["ID Fornitore"] || "N/A",
              totaleGiorniMagazzino: 0,
              countMagazzino: 0,
              inTargetMagazzino: 0,
              totaleGiorniFattura: 0,
              countFattura: 0,
              inTargetFattura: 0,
              target: targetSpedizione,
              ordini: []
            };
          }

          let statusMag = "danger";
          if (diffDaysMag <= targetSpedizione) statusMag = "success";
          else if (diffDaysMag <= targetSpedizione + 1) statusMag = "warning";

          let statusFatt = "none";
          if (diffDaysFatt !== null) {
            if (diffDaysFatt <= targetSpedizione) statusFatt = "success";
            else if (diffDaysFatt <= targetSpedizione + 1) statusFatt = "warning";
            else statusFatt = "danger";
          }

          perFornitore[fornitore].ordini.push({
            idOrdine: e["ID Ordine"],
            numDoc: numDoc,
            articolo,
            precodice,
            dataOrdine: e["Data Creazione"],
            dataEntrata: e["data_entrata"].split(" ")[0],
            dataFattura: stringaFattura ? stringaFattura.split(" ")[0] : null,
            giorniLavorativiReali: diffDaysMag,
            giorniLavorativiFattura: diffDaysFatt,
            statusOrdine: statusMag,
            statusFattura: statusFatt,
            isDopoCutoff
          });

          perFornitore[fornitore].totaleGiorniMagazzino += diffDaysMag;
          perFornitore[fornitore].countMagazzino += 1;
          if (diffDaysMag <= targetSpedizione) perFornitore[fornitore].inTargetMagazzino += 1;

          if (diffDaysFatt !== null) {
            perFornitore[fornitore].totaleGiorniFattura += diffDaysFatt;
            perFornitore[fornitore].countFattura += 1;
            if (diffDaysFatt <= targetSpedizione) perFornitore[fornitore].inTargetFattura += 1;
          }
        });

        const risultato = Object.entries(perFornitore)
          .map(([fornitore, val]) => {
            const mediaMag = val.countMagazzino > 0
              ? Math.round((val.totaleGiorniMagazzino / val.countMagazzino) * 2) / 2
              : null;
            const pctMagazzino = val.countMagazzino > 0
              ? Math.round((val.inTargetMagazzino / val.countMagazzino) * 100)
              : null;

            const mediaFatt = val.countFattura > 0
              ? Math.round((val.totaleGiorniFattura / val.countFattura) * 2) / 2
              : null;
            const pctFattura = val.countFattura > 0
              ? Math.round((val.inTargetFattura / val.countFattura) * 100)
              : null;

            const getPctStyle = (pct) => {
              if (pct === null) return { bg: "#f1f3f5", text: "#adb5bd" };
              if (pct > 75) return { bg: "#e6fcf5", text: "#099268" };
              if (pct >= 50) return { bg: "#fff9db", text: "#f08000" };
              return { bg: "#fff5f5", text: "#e03131" };
            };

            let statusMag = "danger";
            if (mediaMag === null) statusMag = "none";
            else if (mediaMag <= val.target) statusMag = "success";
            else if (mediaMag <= val.target + 1) statusMag = "warning";

            let statusFatt = "danger";
            if (mediaFatt === null) statusFatt = "none";
            else if (mediaFatt <= val.target) statusFatt = "success";
            else if (mediaFatt <= val.target + 1) statusFatt = "warning";

            const ordiniOrdinati = val.ordini.sort((a, b) => b.idOrdine - a.idOrdine);

            const gruppiMap = {};
            ordiniOrdinati.forEach((riga) => {
              const chiaveGruppo = riga.numDoc || `NODOC-${riga.idOrdine}`;
              if (!gruppiMap[chiaveGruppo]) gruppiMap[chiaveGruppo] = [];
              gruppiMap[chiaveGruppo].push(riga);
            });

            const gruppiOrdini = Object.values(gruppiMap)
              .map((righe) => {
                const righeOrdinate = righe.sort((a, b) => new Date(a.dataEntrata) - new Date(b.dataEntrata));
                const dateUniche = [...new Set(righeOrdinate.map((r) => r.dataEntrata))];
                return {
                  numDoc: righeOrdinate[0].numDoc,
                  idOrdine: righeOrdinate[0].idOrdine,
                  dataOrdine: righeOrdinate[0].dataOrdine,
                  isDopoCutoff: righeOrdinate[0].isDopoCutoff,
                  righe: righeOrdinate,
                  isMultiData: dateUniche.length > 1,
                  dateUniche
                };
              })
              .sort((a, b) => b.idOrdine - a.idOrdine);

            return {
              fornitore,
              idFornitore: val.idFornitore,
              target: val.target,
              countMagazzino: val.countMagazzino,
              inTargetMagazzino: val.inTargetMagazzino,
              mediaMagazzino: mediaMag !== null ? mediaMag.toFixed(1) + " gg" : "--",
              pctMagazzino,
              styleMag: getPctStyle(pctMagazzino),
              statusMag,
              countFattura: val.countFattura,
              mediaFattura: mediaFatt !== null ? mediaFatt.toFixed(1) + " gg" : "--",
              pctFattura,
              styleFatt: getPctStyle(pctFattura),
              statusFatt,
              ordini: ordiniOrdinati,
              gruppiOrdini
            };
          })
          .sort((a, b) => a.fornitore.localeCompare(b.fornitore));

        setData(risultato);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore nel caricamento dei dati JSON:", err);
        setLoading(false);
      });
  }, []);

  const handleOpenDettaglio = (fornitoreObj) => {
    setSelectedFornitore(fornitoreObj);
    setShowDetailModal(true);
  };

const renderBadgeStatus = (status, mini = true) => {
  const config = {
    success: { bg: "success-subtle", text: "text-success", border: "border-success-alpha", icon: <FiCheckCircle size={mini ? 10 : 13} />, label: "In Target" },
    warning: { bg: "warning-subtle", text: "text-warning", border: "border-warning-alpha", icon: <FiClock size={mini ? 10 : 13} />, label: "Alert" },
    danger: { bg: "danger-subtle", text: "text-danger", border: "border-danger-alpha", icon: <FiAlertCircle size={mini ? 10 : 13} />, label: "Ritardo" }
  };

  const current = config[status];
  if (!current) return <span className="text-muted text-center d-block">--</span>;

  return (
    <Badge bg={current.bg} className={`${mini ? 'status-badge-mini' : 'status-badge-md'} ${current.text} border ${current.border} text-uppercase`}>
      {current.icon} {current.label}
    </Badge>
  );
};
  const renderTooltip = (props) => (
    <Tooltip id="info-performance-tooltip" {...props}>
      <div style={{ textAlign: 'left', fontSize: '0.7rem', padding: '5px' }}>
        <strong>MONITORAGGIO PERFORMANCE COMPARATIVO:</strong><br/>
        • <strong>Magazzino:</strong> Giorni lavorativi tra Ordine e Data Entrata.<br/>
        • <strong>Fattura / Rimesso:</strong> Giorni lavorativi tra Ordine e Data Fattura/Rimesso.<br/>
        • <strong>Target:</strong> Giorni Spedizione - 1 (con +1gg per ordini post 16:00).
      </div>
    </Tooltip>
  );

  const formattaDataConOra = (dataString, includiOra = false) => {
    if (!dataString) return "N/A";
    const data = new Date(dataString);
    if (isNaN(data.getTime())) return dataString;

    const opzioni = { day: '2-digit', month: '2-digit', year: 'numeric' };
    if (includiOra) {
      opzioni.hour = '2-digit';
      opzioni.minute = '2-digit';
    }
    return data.toLocaleDateString('it-IT', opzioni);
  };

  const renderTableContent = () => (
    <table className="table table-hover mb-0" style={{ fontSize: "0.70rem" }}>
      <thead className="bg-white sticky-top" style={{ zIndex: 1, boxShadow: "0 2px 2px -1px rgba(0,0,0,0.05)" }}>
        <tr>
          <th className="border-0 text-muted ps-3" style={{ verticalAlign: "middle" }}>FORNITORE</th>
          <th className="border-0 text-muted text-center border-end" style={{ verticalAlign: "middle", width: "70px" }}>TARGET</th>
          <th className="border-0 text-primary text-center bg-light border-end" style={{ fontSize: "0.65rem", fontWeight: "800", borderBottom: "2px solid #0d6efd" }}>
            ENTRATA MAGAZZINO
          </th>
          <th className="border-0 text-dark text-center" style={{ fontSize: "0.65rem", fontWeight: "800", borderBottom: "2px solid #495057", backgroundColor: "#f8f9fa" }}>
            FATTURAZIONE / RIMESSO
          </th>
        </tr>
      </thead>
      <tbody>
        {data.map((f, i) => (
          <tr key={i} style={{ verticalAlign: "middle", cursor: "pointer" }} onClick={() => handleOpenDettaglio(f)}>
            <td className="ps-3">
              <div className="fw-bold" style={{ lineHeight: "1.2", color: "#333", maxWidth: "170px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {f.fornitore}
              </div>
              <div style={{ fontSize: "0.58rem", color: "#999" }}>
                ID: {f.idFornitore} • Righe: {f.countMagazzino}
              </div>
            </td>

            <td className="text-center text-muted border-end fw-bold">{f.target} gg</td>

            {/* Colonna 1: Entrata Magazzino */}
            <td className="text-center bg-light border-end">
              <span className="fw-bold text-primary d-block" style={{ fontSize: "0.75rem" }}>{f.mediaMagazzino}</span>
              {f.pctMagazzino !== null ? (
                <div className="badge-small-pct mt-1 me-1" style={{ backgroundColor: f.styleMag.bg, color: f.styleMag.text }}>
                  {f.pctMagazzino}%
                </div>
              ) : null}
              {renderBadgeStatus(f.statusMag)}
            </td>

            {/* Colonna 2: Data Fattura / Rimesso */}
            <td className="text-center" style={{ backgroundColor: "#f8f9fa" }}>
              <span className="fw-bold text-dark d-block" style={{ fontSize: "0.75rem" }}>{f.mediaFattura}</span>
              {f.pctFattura !== null ? (
                <div className="badge-small-pct mt-1 me-1" style={{ backgroundColor: f.styleFatt.bg, color: f.styleFatt.text }}>
                  {f.pctFattura}%
                </div>
              ) : null}
              {renderBadgeStatus(f.statusFatt)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="card tb" style={{ borderRadius: "10px", border: "1px solid #dee2e6", height: "450px", display: "flex", flexDirection: "column", overflow: "hidden", backgroundColor: "#fff" }}>
      <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white" style={{ zIndex: 110 }}>
        <div className="d-flex align-items-center" onClick={() => setShowFullscreenModal(true)} style={{ cursor: "pointer" }}>
          <span style={{ fontWeight: "800", fontSize: "0.75rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
            PERFORMANCE FORNITORI (MAGAZZINO VS FATTURAZIONE)
          </span>
          <OverlayTrigger placement="bottom" overlay={renderTooltip}>
            <span className="ms-2" onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer" }}><FiInfo size={14} color="#adb5bd" /></span>
          </OverlayTrigger>
        </div>
      </div>

      <div className="scroll-container" style={{ flexGrow: 1, overflowY: "auto" }}>
        <style>{`
          .scroll-container::-webkit-scrollbar { width: 4px; }
          .scroll-container::-webkit-scrollbar-thumb { background: #ccc; border-radius: 10px; }
          .badge-small-pct { padding: 3px 5px; font-size: 0.60rem; font-weight: 800; border-radius: 4px; display: inline-block; text-align: center; }
          .status-badge-mini { padding: 3px 5px; font-size: 0.58rem; display: inline-flex; align-items: center; gap: 3px; letter-spacing: 0.5px; white-space: nowrap; }
          .status-badge-md { padding: 6px 10px; font-size: 0.7rem; display: inline-flex; align-items: center; gap: 4px; letter-spacing: 0.5px; font-weight: 700; }
          .table td { padding: 10px 6px !important; }
          .supplier-modal-content {
            max-width: 950px !important;
            width: 100% !important;
            margin: 0 auto !important;
            border-radius: 16px !important;
            box-shadow: 0 15px 40px rgba(0,0,0,0.12) !important;
            overflow: hidden;
          }
          .kpi-supplier-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 10px;
          }
          .kpi-supplier-card {
            padding: 12px;
            border-radius: 10px;
            background: #fdfdfd;
            border: 1px solid #eaeaea;
          }
          .kpi-supplier-title { font-size: 0.60rem; font-weight: 700; color: #8c8c8c; text-transform: uppercase; margin-bottom: 4px; letter-spacing: 0.5px;}
          .kpi-supplier-value { font-size: 0.95rem; font-weight: 800; color: #1a1a1a; }
        `}</style>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" variant="danger" size="sm" />
          </div>
        ) : (
          renderTableContent()
        )}
      </div>

      {/* MODALE TUTTO SCHERMO */}
      <Modal show={showFullscreenModal} onHide={() => setShowFullscreenModal(false)} fullscreen={true} animation={true}>
        <Modal.Body className="p-0 d-flex flex-column" style={{ background: "#f8f9fa" }}>
          <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm" style={{ zIndex: 110 }}>
            <div className="d-flex align-items-center">
              <span style={{ fontWeight: "800", fontSize: "0.9rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
                ANALISI COMPARATIVA FORNITORI (MAGAZZINO VS FATTURAZIONE)
              </span>
              <OverlayTrigger placement="bottom" overlay={renderTooltip}>
                <span style={{ cursor: "pointer" }}><FiInfo size={16} color="#adb5bd" /></span>
              </OverlayTrigger>
            </div>
            <Button variant="outline-dark" className="d-flex align-items-center gap-1 border-0" onClick={() => setShowFullscreenModal(false)} style={{ borderRadius: "10px", height: "38px" }}>
              <FiX size={16} /> Chiudi
            </Button>
          </div>
          <div className="flex-grow-1 p-4" style={{ overflowY: "auto" }}>
            <div className="bg-white p-3 shadow-sm rounded-4 border scroll-container">
              {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                  <Spinner animation="border" variant="danger" size="sm" />
                </div>
              ) : (
                renderTableContent()
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* MODALE DETTAGLIO FORNITORE */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        centered
        contentClassName="supplier-modal-content"
      >
        <Modal.Header closeButton className="px-4 pt-4 pb-2 border-0 bg-white">
          <Modal.Title className="fw-extrabold d-flex align-items-center gap-2" style={{ fontSize: "1.1rem", color: "#0d6efd", letterSpacing: "0.3px" }}>
            <FiList size={22} /> TRACCIABILITÀ COMPLETA FORNITORE
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-white px-4 pb-3" style={{ overflow: "hidden" }}>
          <div className="p-3 mb-4 rounded-3 d-flex justify-content-between align-items-center" style={{ background: "#f8f9fa", borderLeft: "4px solid #0d6efd" }}>
            <div>
              <small className="text-muted d-block fw-bold" style={{ fontSize: "0.6rem", letterSpacing: "0.5px" }}>RAGIONE SOCIALE</small>
              <span className="fw-bold text-dark d-block" style={{ fontSize: "0.95rem" }}>{selectedFornitore?.fornitore}</span>
              <small className="text-muted" style={{ fontSize: "0.65rem" }}>ID Fornitore Interno: <strong>{selectedFornitore?.idFornitore}</strong></small>
            </div>
            <div className="text-end">
              <small className="text-muted d-block fw-bold" style={{ fontSize: "0.6rem", letterSpacing: "0.5px" }}>TARGET MEDIO</small>
              <Badge bg="dark" className="fw-bold" style={{ fontSize: "0.75rem" }}>{selectedFornitore?.target} GIORNI</Badge>
            </div>
          </div>

          <div className="kpi-supplier-grid mb-4">
            <div className="kpi-supplier-card" style={{ borderTop: "3px solid #0d6efd" }}>
              <div className="kpi-supplier-title"><FiShoppingBag className="me-1" /> Carico Righe</div>
              <div className="kpi-supplier-value text-primary">{selectedFornitore?.countMagazzino} totali</div>
            </div>
            <div className="kpi-supplier-card" style={{ borderTop: "3px solid #198754" }}>
              <div className="kpi-supplier-title"><FiCheckCircle className="me-1 text-success" /> Target Magazzino</div>
              <div className="kpi-supplier-value text-success">{selectedFornitore?.mediaMagazzino} ({selectedFornitore?.pctMagazzino}%)</div>
            </div>
            <div className="kpi-supplier-card" style={{ borderTop: "3px solid #495057" }}>
              <div className="kpi-supplier-title"><FiFileText className="me-1 text-dark" /> Media Fattura</div>
              <div className="kpi-supplier-value text-dark">{selectedFornitore?.mediaFattura} ({selectedFornitore?.pctFattura}%)</div>
            </div>
            <div className="kpi-supplier-card" style={{ borderTop: "3px solid #f08000" }}>
              <div className="kpi-supplier-title"><FiTrendingUp className="me-1" /> Performance</div>
              <div className="kpi-supplier-value">{renderBadgeStatus(selectedFornitore?.statusMag)}</div>
            </div>
          </div>

          <span className="d-block fw-extrabold text-muted mb-2" style={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}>REGISTRO DETTAGLIATO CONTRONTO DATE</span>

          <div style={{ maxHeight: "350px", overflowY: "auto", border: "1px solid #eaeaea", borderRadius: "6px" }}>
            <Table hover className="mb-0 align-middle" style={{ fontSize: "0.7rem", width: "100%", tableLayout: "fixed", minWidth: "820px" }}>
              <thead className="bg-light sticky-top" style={{ zIndex: 1, top: 0 }}>
                <tr style={{ borderBottom: "2px solid #eaeaea" }}>
                  <th className="py-2 ps-2 text-muted bg-light" style={{ width: "16%" }}>ID / DOC ORDINE</th>
                  <th className="py-2 text-muted text-center bg-light" style={{ width: "14%" }}>ARTICOLO</th>
                  <th className="py-2 text-muted text-center bg-light" style={{ width: "16%" }}>DATA ORDINE</th>
                  <th className="py-2 text-muted text-center bg-light border-start" style={{ width: "18%" }}>MAGAZZINO</th>
                  <th className="py-2 text-muted text-center bg-light border-start" style={{ width: "18%" }}>FATTURA / RIMESSO</th>
                  <th className="py-2 text-muted text-center pe-2 bg-light border-start" style={{ width: "18%" }}>ESITO MAG / FATT</th>
                </tr>
              </thead>
              <tbody>
                {selectedFornitore?.gruppiOrdini?.map((gruppo, gIdx) => (
                  gruppo.righe.map((riga, rIdx) => (
                    <tr key={`${gIdx}-${rIdx}`} style={{ borderBottom: rIdx === gruppo.righe.length - 1 ? "1px solid #dee2e6" : "1px dashed #f1f3f5" }}>
                      {rIdx === 0 && (
                        <td className="py-2.5 ps-2 text-truncate" rowSpan={gruppo.righe.length}>
                          <div className="fw-bold text-dark text-truncate">OF4G: {gruppo.numDoc || "N/A"}</div>
                          <div className="text-muted text-truncate" style={{ fontSize: "0.6rem" }}>ID: {gruppo.idOrdine}</div>
                        </td>
                      )}
                      <td className="py-2.5 text-center text-secondary text-truncate" title={riga.articolo}>{riga.articolo}</td>
                      {rIdx === 0 && (
                        <td className="py-2.5 text-center text-secondary fw-medium" rowSpan={gruppo.righe.length}>
                          <div>{formattaDataConOra(gruppo.dataOrdine, true)}</div>
                          {gruppo.isDopoCutoff && (
                            <Badge bg="warning" text="dark" className="mt-1" style={{ fontSize: "0.55rem", padding: "2px 4px" }}>
                              Dopo Cutoff
                            </Badge>
                          )}
                        </td>
                      )}
                      {/* Magazzino */}
                      <td className="py-2.5 text-center border-start">
                        <div>{formattaDataConOra(riga.dataEntrata, false)}</div>
                        <span className="fw-bold text-primary" style={{ fontSize: "0.68rem" }}>{riga.giorniLavorativiReali} gg</span>
                      </td>
                      {/* Fattura / Rimesso */}
                      <td className="py-2.5 text-center border-start">
                        <div>{formattaDataConOra(riga.dataFattura, false)}</div>
                        <span className="fw-bold text-dark" style={{ fontSize: "0.68rem" }}>
                          {riga.giorniLavorativiFattura !== null ? `${riga.giorniLavorativiFattura} gg` : "--"}
                        </span>
                      </td>
                      {/* Esiti */}
                      <td className="py-2.5 text-center border-start pe-2">
                        <div className="d-flex flex-column gap-1 align-items-center">
                          {renderBadgeStatus(riga.statusOrdine, true)}
                          {renderBadgeStatus(riga.statusFattura, true)}
                        </div>
                      </td>
                    </tr>
                  ))
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light border-0 justify-content-end py-2.5" />
      </Modal>
    </div>
  );
};

export default MediaFornitoriCard;